import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  input,
  output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FileUploadControl } from '@app/models/form.models';
import { DownloadService } from '@app/services/file/download.service';
import { TranslatePipe } from '@ngx-translate/core';
import { FormArrayTableComponent } from 'src/app/components/common/form-array-table/form-array-table.component';
import { ValidatedInputComponent } from '../validated-input/validated-input.component';

@Component({
  selector: 'app-upload-files',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    FormArrayTableComponent,
    ValidatedInputComponent,
  ],
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.scss',
})
export class UploadFilesComponent {
  private readonly downloadService = inject(DownloadService);

  public formControls = input.required<FormArray<FormGroup<FileUploadControl>>>();
  public title = input.required<string>();
  public canAdd = input(false);
  public showIsPublicCheckbox = input(false);

  public fileAdded = output<File>();
  public fileRemoved = output<number>();
  public createNewFileGroup = output<void>();

  @ViewChildren('fileInputs') fileInputsList!: QueryList<
    ElementRef<HTMLInputElement>
  >;

  public onUploadClick(index: number): void {
    this.fileInputsList.get(index)?.nativeElement?.click();
  }

  public onReplaceClick(index: number): void {
    this.fileInputsList.get(index)?.nativeElement?.click();
  }

  public hasExistingFile(itemGroup: FormGroup<FileUploadControl>): boolean {
    const selectedFile = itemGroup.controls.file.value;
    const title = itemGroup.controls.title.value?.trim() ?? '';
    return Boolean(selectedFile || title);
  }

  public isPreviouslyUploadedFile(
    itemGroup: FormGroup<FileUploadControl>,
  ): boolean {
    const selectedFile = itemGroup.controls.file.value;
    const title = itemGroup.controls.title.value?.trim() ?? '';
    const description = itemGroup.controls.description.value?.trim() ?? '';
    return !selectedFile && Boolean(title || description);
  }

  public canDownloadUploadedFile(
    itemGroup: FormGroup<FileUploadControl>,
  ): boolean {
    const fileId = itemGroup.controls.id.value?.trim();
    return this.isPreviouslyUploadedFile(itemGroup) && Boolean(fileId);
  }

  public getDisplayedFileName(itemGroup: FormGroup<FileUploadControl>): string {
    return itemGroup.controls.file.value?.name ?? itemGroup.controls.title.value ?? '';
  }

  public onDownloadClick(
    event: MouseEvent,
    itemGroup: FormGroup<FileUploadControl>,
  ): void {
    event.stopPropagation();
    const fileId = itemGroup.controls.id.value?.trim();
    if (!fileId) {
      return;
    }
    const fallbackName = itemGroup.controls.title.value?.trim() || 'document';
    this.downloadService.downloadFileById(fileId, fallbackName).subscribe();
  }

  public onFileSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      const itemGroup = this.formControls().at(
        index,
      ) as FormGroup<FileUploadControl>;
      itemGroup.patchValue({
        file: file,
        title: file.name,
      });
      itemGroup.controls.file.markAsDirty();
      itemGroup.controls.title.markAsDirty();
      itemGroup.markAsDirty();
      this.formControls().markAsDirty();
      this.fileAdded.emit(file);
    }
  }

  public onFileRemove(event: MouseEvent, index: number): void {
    event.stopPropagation();
    const itemGroup = this.formControls().at(
      index,
    ) as FormGroup<FileUploadControl>;
    itemGroup.patchValue({ file: null, title: '' });
    itemGroup.controls.file.markAsDirty();
    itemGroup.controls.title.markAsDirty();
    itemGroup.markAsDirty();
    this.formControls().markAsDirty();
    this.fileRemoved.emit(index);
  }

  public showTitleError(itemGroup: FormGroup<FileUploadControl>): boolean {
    return itemGroup.controls.title.invalid && itemGroup.controls.title.touched;
  }

  public showFileError(itemGroup: FormGroup<FileUploadControl>): boolean {
    return itemGroup.controls.file.invalid && itemGroup.controls.file.touched;
  }

  public onFormSubmit(event: Event): void {
    event.preventDefault();
  }

  public onEnterKey(event: Event): void {
    event.preventDefault();
  }
}
